import dayjs from "dayjs";

import { formatProductOptionLabel } from "../../../routes/pricing-reports/constants";
import { getCountFromBeefAiAggregateRow } from "../../../routes/pricing-reports/productSnapshotChartUtils";
import {
  CHART_COLORS,
  compareMonthKeys,
  monthKeyFromRow,
  resolveInsightsBarChartColor,
} from "../../../routes/pricing-reports/pricingChartsUtils";
import { normalizeAggregateProductId } from "../../../routes/pricing-reports/pricingReportUtils";
import { productPriceHistoryCountByIdFromAggregateRows } from "../../../routes/pricing-reports/pricingReportProductFilter";

export const TOP_PRODUCT_MIN_COUNT = 50;
export const TOP_PRODUCT_SLICE_LIMIT = 10;
export const FREQUENCY_PIE_SLICE_LIMIT = 12;
export const RETAILER_SPREAD_BAR_LIMIT = 12;

/**
 * Canonical under-utilized cuts. Phrases are matched against normalized Beef AI
 * `product.name` / `raw_product_name` (the same catalog as the Pricing Report dropdown).
 */
export const UNDER_UTILIZED_CUTS = [
  {
    key: "outside-skirt",
    label: "Outside Skirt",
    phrases: ["outside skirt"],
  },
  {
    key: "clod-petite-tender",
    label: "Clod / Petite Tender",
    phrases: ["petite tender", "clod petite tender", "shoulder clod petite tender"],
  },
  {
    key: "inside-skirt",
    label: "Inside Skirt",
    phrases: ["inside skirt"],
  },
  {
    key: "bottom-sirloin-tri-tip",
    label: "Bottom Sirloin Tri-Tip",
    phrases: ["bottom sirloin tri tip", "tri tip"],
  },
  {
    key: "chuck-eye-roll",
    label: "Chuck Eye Roll",
    phrases: ["chuck eye roll"],
  },
  {
    key: "flank-steak",
    label: "Flank Steak",
    phrases: ["flank steak"],
  },
];

export function normalizeCutSearchText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function productSearchHaystack(product) {
  return normalizeCutSearchText(`${product?.name || ""} ${product?.raw_product_name || ""}`);
}

export function matchUnderUtilizedCut(product) {
  const haystack = productSearchHaystack(product);
  if (!haystack) {
    return null;
  }

  for (const cut of UNDER_UTILIZED_CUTS) {
    if (cut.phrases.some((phrase) => haystack.includes(phrase))) {
      return cut;
    }
  }

  return null;
}

export function compareFrequencyRows(left, right) {
  const countDelta = (right?.count || 0) - (left?.count || 0);
  if (countDelta !== 0) {
    return countDelta;
  }
  return String(left?.productName || "").localeCompare(String(right?.productName || ""), undefined, {
    sensitivity: "base",
  });
}

/**
 * Catalog products (Pricing Report dropdown) joined to `price_history` counts.
 *
 * @param {any[]} catalogProducts
 * @param {Map<string, number> | any[]} countByProductIdOrAggregateRows
 * @returns {{ id: string, productName: string, count: number, product: any }[]}
 */
export function buildProductFrequencyRows(catalogProducts, countByProductIdOrAggregateRows) {
  const countByProductId =
    countByProductIdOrAggregateRows instanceof Map
      ? countByProductIdOrAggregateRows
      : productPriceHistoryCountByIdFromAggregateRows(
          countByProductIdOrAggregateRows,
          normalizeAggregateProductId
        );

  const uniqueProductsById = new Map();
  for (const catalogProduct of catalogProducts || []) {
    if (!catalogProduct?.id) {
      continue;
    }
    uniqueProductsById.set(String(catalogProduct.id), catalogProduct);
  }

  return [...uniqueProductsById.values()]
    .map((catalogProduct) => {
      const id = String(catalogProduct.id);
      const count = Number(countByProductId.get(id) ?? 0);
      return {
        id,
        productName: formatProductOptionLabel(catalogProduct) || id,
        count: Number.isFinite(count) ? count : 0,
        product: catalogProduct,
      };
    })
    .sort(compareFrequencyRows);
}

export function buildUnderUtilizedCutRows(frequencyRows) {
  const rowsByCutKey = new Map(
    UNDER_UTILIZED_CUTS.map((cut) => [
      cut.key,
      {
        key: cut.key,
        label: cut.label,
        count: 0,
        productNames: [],
      },
    ])
  );

  for (const row of frequencyRows || []) {
    const cut = matchUnderUtilizedCut(row?.product || row);
    if (!cut) {
      continue;
    }
    const cutRow = rowsByCutKey.get(cut.key);
    if (!cutRow) {
      continue;
    }
    cutRow.count += Number(row.count) || 0;
    if (row.productName) {
      cutRow.productNames.push(row.productName);
    }
  }

  return UNDER_UTILIZED_CUTS.map((cut) => rowsByCutKey.get(cut.key));
}

export function takeTopCountedRows(rows, limit) {
  return [...(rows || [])]
    .sort((left, right) => (right.count || 0) - (left.count || 0))
    .slice(0, limit);
}

export function limitCountedSlices(rows, { limit = FREQUENCY_PIE_SLICE_LIMIT, labelKey = "label" } = {}) {
  const sorted = [...(rows || [])].sort((left, right) => (right.count || 0) - (left.count || 0));
  if (sorted.length <= limit) {
    return sorted;
  }

  const visible = sorted.slice(0, limit - 1);
  const remainder = sorted.slice(limit - 1);
  const otherCount = remainder.reduce((sum, row) => sum + (Number(row.count) || 0), 0);
  return [
    ...visible,
    {
      [labelKey]: "Other",
      label: "Other",
      productName: "Other",
      name: "Other",
      count: otherCount,
    },
  ];
}

function sliceColors(count) {
  return Array.from({ length: count }, (_, index) => CHART_COLORS[index % CHART_COLORS.length]);
}

export function buildCountPieChart(rows, { labelKey = "label", datasetLabel = "Count" } = {}) {
  const slices = (rows || []).filter((row) => (Number(row.count) || 0) > 0);
  return {
    labels: slices.map((row) => row[labelKey] || row.label || row.productName || row.name || ""),
    datasets: [
      {
        label: datasetLabel,
        data: slices.map((row) => Number(row.count) || 0),
        backgroundColor: sliceColors(slices.length),
        borderWidth: 1,
      },
    ],
  };
}

export function buildTopProductPieChart(frequencyRows) {
  const topRows = (frequencyRows || []).filter((row) => row.count >= TOP_PRODUCT_MIN_COUNT);
  return buildCountPieChart(takeTopCountedRows(topRows, TOP_PRODUCT_SLICE_LIMIT), {
    labelKey: "productName",
    datasetLabel: "Price observations",
  });
}

export function buildUnderUtilizedCutsPieChart(frequencyRows) {
  return buildCountPieChart(buildUnderUtilizedCutRows(frequencyRows), {
    labelKey: "label",
    datasetLabel: "Price observations",
  });
}

export function buildRetailerSpreadRows(retailerCountByName) {
  const rows = [];
  for (const [name, count] of retailerCountByName || []) {
    const trimmed = String(name || "").trim();
    const numericCount = Number(count) || 0;
    if (!trimmed || numericCount <= 0) {
      continue;
    }
    rows.push({ name: trimmed, count: numericCount });
  }
  return rows.sort((left, right) => {
    const countDelta = right.count - left.count;
    if (countDelta !== 0) {
      return countDelta;
    }
    return left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
  });
}

export function buildRetailerSpreadBarChart(retailerCountByName) {
  const limited = limitCountedSlices(buildRetailerSpreadRows(retailerCountByName), {
    limit: RETAILER_SPREAD_BAR_LIMIT,
    labelKey: "name",
  });

  return {
    labels: limited.map((row) => row.name || row.label),
    datasets: [
      {
        label: "Price observations",
        data: limited.map((row) => Number(row.count) || 0),
        backgroundColor: sliceColors(limited.length),
      },
    ],
  };
}

export function buildMonthlyFrequencyBarChart(aggregateRows) {
  const countByMonth = new Map();
  for (const row of aggregateRows || []) {
    const monthKey = monthKeyFromRow(row);
    if (!monthKey) {
      continue;
    }
    const count = getCountFromBeefAiAggregateRow(row);
    countByMonth.set(monthKey, (countByMonth.get(monthKey) || 0) + count);
  }

  const monthKeys = [...countByMonth.keys()].sort(compareMonthKeys);
  return {
    labels: monthKeys.map((monthKey) => dayjs(`${monthKey}-01`).format("MMM YYYY")),
    datasets: [
      {
        label: "Price observations",
        data: monthKeys.map((monthKey) => countByMonth.get(monthKey) || 0),
        backgroundColor: resolveInsightsBarChartColor(),
      },
    ],
  };
}
